import { useMemo } from 'react';

import {
  ArcElement,
  CategoryScale,
  Chart,
  ChartData,
  LinearScale,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

// eslint-disable-next-line import/no-relative-packages
import { GenerationDataPoints } from '../../../../../universal-dashboard-api/src/Data-Processor/Entities/Interfaces/GenerationBreakdownDAO';
import { formatPercentage } from '../../../utils/formatters';
import { getVariable } from '../../../utils/style';
import { Card } from '../Card';
import { OTHER_BG_COLOR, CHART_OPTIONS, LABELS } from './const';
import { filterOnlyRenewableGeneration } from './utils';

Chart.register(ArcElement, CategoryScale, LinearScale);

type GenerationCardProps = {
  data?: GenerationDataPoints[];
  loading?: boolean;
};

export function GenerationCard({ data = [], loading }: GenerationCardProps) {
  const backgroundColors = useMemo(
    () => [
      getVariable('--green-60'),
      getVariable('--green-40'),
      getVariable('--green-20'),
      OTHER_BG_COLOR,
    ],
    [],
  );

  const renewables = useMemo(() => filterOnlyRenewableGeneration(data), [data]);

  const renewablesTotal = renewables.reduce(
    (acc, curr) => acc + curr.generation,
    0,
  );

  const chartData: ChartData<'doughnut'> = useMemo(
    () => ({
      datasets: [
        {
          label: 'Dataset 1',
          data: [
            ...renewables.map(({ generation }) => generation),
            1 - renewablesTotal,
          ],
          borderWidth: 0,
          backgroundColor: backgroundColors,
        },
      ],
    }),
    [backgroundColors, renewables, renewablesTotal],
  );

  return (
    <Card className="relative pb-4">
      <div className="flex items-center justify-center gap-2">
        <h2>GB generation mix</h2>
      </div>
      <div className="h-20">
        {!loading && <Doughnut data={chartData} options={CHART_OPTIONS} />}
      </div>
      {loading ? (
        <div className="h-20" />
      ) : (
        <div className="flex flex-col items-center gap-2 px-2 text-sm">
          <table className="w-28">
            <tbody>
              {renewables.map(({ fuelType, generation }, i) => (
                <tr key={fuelType}>
                  <td className="flex items-center gap-2">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: backgroundColors[i] }}
                    />
                    {LABELS[fuelType] ?? 'Unknown'}
                  </td>
                  <td className="text-right">{formatPercentage(generation)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>{formatPercentage(renewablesTotal)} renewables</p>
        </div>
      )}
    </Card>
  );
}
